/**
 * Device Modals - Modular Component System
 * 
 * This module provides a modular UI component system for device control modals.
 * 
 * Structure:
 * - traits/      - Trait-based control components (OnOff, Brightness, Mode, etc.)
 * - attributes/  - Attribute-based display components (SensorDisplay, SensorChart, etc.)
 * - templates/   - DeviceType-specific modal templates (Dehumidifier, Switch, Sensor, etc.)
 * 
 * Each component has multiple style variants:
 * - Style01, Style02, Style03... for trait/attribute components
 * - Template01, Template02... for device templates
 */

import React, { Suspense, lazy, useMemo, useState, useEffect } from 'react';
import { DeviceTemplateProps, DeviceModalConfig, IntelliSpaceDevice } from './types';
import { getTemplateForDeviceType } from './templates';
import { getDeviceModalTheme, getDefaultConfig } from '../../services/deviceModalThemeService';

// Re-export all components for external use
export * from './traits';
export * from './attributes';
export * from './templates';
export * from './types';

// Loading fallback component
const LoadingFallback: React.FC = () => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
    <div className="bg-slate-800 rounded-2xl p-8 flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      <span className="text-slate-300 text-sm">載入中...</span>
    </div>
  </div>
);

// Default template configuration (used as fallback)
const DEFAULT_CONFIG: DeviceModalConfig = {
  templateId: 'Template01',
  traitStyles: {
    OnOff: 'Style02',
    Brightness: 'Style01',
    HumiditySetting: 'Style02',
    FanSpeed: 'Style01',
    Mode: 'Style01',
    Timer: 'Style01',
    CustomToggles: 'Style01'
  },
  attributeStyles: {
    SensorDisplay: 'Style01',
    SensorChart: 'Style02',
    StatusIndicator: 'Style02'
  }
};

/**
 * Dynamic Device Modal Renderer
 * 
 * Automatically selects and renders the appropriate template
 * based on device type and configuration.
 * 
 * Configuration Priority:
 * 1. Props config (highest priority)
 * 2. Firestore user preferences
 * 3. Default config (fallback)
 */
export interface DeviceModalRendererProps extends Omit<DeviceTemplateProps, 'config'> {
  config?: Partial<DeviceModalConfig>;
  /** Skip loading theme from Firestore (use for preview mode) */
  skipFirestoreTheme?: boolean;
}

export const DeviceModalRenderer: React.FC<DeviceModalRendererProps> = ({
  device,
  config,
  skipFirestoreTheme = false,
  ...props
}) => {
  // State for Firestore theme
  const [firestoreTheme, setFirestoreTheme] = useState<DeviceModalConfig | null>(null);
  const [isLoadingTheme, setIsLoadingTheme] = useState(!skipFirestoreTheme);

  // Load theme from Firestore on mount
  useEffect(() => {
    if (skipFirestoreTheme) {
      setIsLoadingTheme(false);
      return;
    }

    let cancelled = false;
    
    const loadTheme = async () => {
      try {
        const theme = await getDeviceModalTheme(device.deviceType);
        if (!cancelled) {
          setFirestoreTheme(theme);
        }
      } catch (error) {
        // Silently fail and use default config
        console.warn('Failed to load device modal theme:', error);
      } finally {
        if (!cancelled) {
          setIsLoadingTheme(false);
        }
      }
    };

    loadTheme();

    return () => {
      cancelled = true;
    };
  }, [device.deviceType, skipFirestoreTheme]);

  // Merge config: props > firestore > default
  const mergedConfig = useMemo(() => {
    // Use firestore theme if available, otherwise use template-specific default
    const templateId = firestoreTheme?.templateId || config?.templateId;
    const baseConfig = firestoreTheme || getDefaultConfig(device.deviceType, templateId);
    return {
      ...baseConfig,
      ...config,
      traitStyles: { 
        ...DEFAULT_CONFIG.traitStyles, 
        ...baseConfig.traitStyles,
        ...config?.traitStyles 
      },
      attributeStyles: { 
        ...DEFAULT_CONFIG.attributeStyles, 
        ...baseConfig.attributeStyles,
        ...config?.attributeStyles 
      }
    };
  }, [config, firestoreTheme, device.deviceType]);

  // Dynamically load template based on device type
  const TemplateComponent = useMemo(() => {
    return lazy(() => getTemplateForDeviceType(device.deviceType));
  }, [device.deviceType]);

  // Show loading while fetching theme (optional - can be removed for faster initial render)
  if (isLoadingTheme) {
    return <LoadingFallback />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <TemplateComponent
        device={device}
        config={mergedConfig}
        {...props}
      />
    </Suspense>
  );
};

// Legacy exports for backward compatibility
export { default as DehumidifierModal } from './templates/Dehumidifier';
export { default as SwitchModal } from './templates/Switch';
export { default as SensorModal } from './templates/Sensor';
export { default as GenericModal } from './templates/Generic';
export { default as HvacModal } from './templates/Hvac';
export { default as FanModal } from './templates/Fan';

// Default export
export default DeviceModalRenderer;
